// Farbrevisionen im Auftrag (docs/architecture/color-catalog-revisions.md, F2).
// Der Auftrag speichert in `project.colorCatalogBinding` seinen Katalogstand und
// ausdrückliche Bindungen. Beim Laden wird nichts erneuert; nur eine aktive
// Auswahl im Colorpicker (und später der Aktualisieren-Button) bindet an die
// aktuelle Revision. Die Bindung ist ein gemeinsamer Auftragswert und gilt für
// alle Zeichnungsebenen.
import { S } from '../app/app_state.js?v=20260817c';
import { AppRuntime } from '../app/app_runtime.js?v=20260706d';
import {
  colorCatalogState,
  colorRevisionKey,
  configureColorBindingContext,
  configureColorRevisionDataListener,
  configureColorSelectionListener,
  currentColorRevisionNumber,
  resolvedColorRevisionNumber,
  sanitizeColorBinding
} from '../../../colors/color_catalog_registry.js?v=20260720a';
import { markProjectModelChanged } from './project_revision_service.js';

export const COLOR_CATALOG_BINDING_FIELD = 'colorCatalogBinding';
const SCHEMA_VERSION = 1;

function isProject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

// Kontext des geöffneten Auftrags: fehlende Bindung = Erstbestand (Revision 1).
export function projectColorBindingContext(project = S.project) {
  if (!isProject(project)) return null;
  return project[COLOR_CATALOG_BINDING_FIELD] ?? false;
}

// Nur für neu angelegte Aufträge (Modus create). Geladene Aufträge ohne Bindung
// bleiben beim Erstbestand und erhalten hier nichts.
export function initializeNewProjectColorBinding(project) {
  if (!isProject(project) || (project[COLOR_CATALOG_BINDING_FIELD] ?? null) !== null) return false;
  project[COLOR_CATALOG_BINDING_FIELD] = { schemaVersion: SCHEMA_VERSION, state: colorCatalogState(), bindings: {} };
  return true;
}

// Aktive Auswahl: bindet den Auftrag für diese Farbe an die aktuelle Revision.
// Entspricht die aktuelle Revision bereits dem Katalogstand des Auftrags, ist
// keine ausdrückliche Bindung nötig. Alle Elemente derselben Farbe im Auftrag
// folgen gemeinsam.
export function bindSelectedColor(value, project = S.project) {
  if (!isProject(project)) return false;
  const key = colorRevisionKey(value);
  if (!key) return false;
  const stored = project[COLOR_CATALOG_BINDING_FIELD];
  const binding = sanitizeColorBinding(stored) || { state: null, bindings: {} };
  const current = currentColorRevisionNumber(key);
  const before = resolvedColorRevisionNumber(key, binding);
  const fromState = resolvedColorRevisionNumber(key, { state: binding.state, bindings: {} });
  const bindings = { ...binding.bindings };
  if (fromState === current) delete bindings[key];
  else bindings[key] = current;
  const unchanged = Object.keys(bindings).length === Object.keys(binding.bindings).length
    && Object.entries(bindings).every(([bindingKey, revision]) => binding.bindings[bindingKey] === revision);
  if (unchanged) return false;
  project[COLOR_CATALOG_BINDING_FIELD] = { schemaVersion: SCHEMA_VERSION, state: binding.state, bindings };
  if (S.file) S.file.dirty = true;
  if (project === S.project && before !== current) {
    markProjectModelChanged('color-catalog-binding');
    AppRuntime.requestAppRefresh(false, 'color-catalog-binding');
  }
  return true;
}

export function installColorCatalogBinding() {
  configureColorBindingContext(() => projectColorBindingContext(S.project));
  configureColorSelectionListener(value => bindSelectedColor(value, S.project));
  // Nachgeladene ältere Revisionen ändern die Darstellung ohne Projektänderung.
  configureColorRevisionDataListener(() => {
    markProjectModelChanged('color-catalog-revisions-loaded');
    AppRuntime.requestAppRefresh(false, 'color-catalog-revisions-loaded');
  });
}
